import { useContext, useEffect, useMemo } from "react";
import { useSetModuleFavorite, useSetModuleUnfavorite } from "../../shared/hooks/useModules";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { IModuleProps } from "./Module.interface";
import { Outlet, useOutlet, useParams } from "react-router-dom";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import FavoriteButton from "../../components/FavoriteButton/FavoriteButton";
import Placeholder from "./../../shared/assets/imgs/placeholder.svg";
import { getFromattedPath, handleFileDownload, markdownToHtml } from "../../shared/helpers/utils";
import Link from "../../components/Link/Link";
import UnitCard from "../../components/UnitCard/UnitCard";
import { CONTENT_BASE_URL } from "../../shared/constants";
import { routes } from "../../shared/router/routes";
import ModuleCard from "../../components/ModuleCard/ModuleCard";
import Button from "../../components/Button/Button";

function Module(props: IModuleProps) {
    const outlet = useOutlet();
    const { translate } = useContext(TranslationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { pathwayModules, module, setUpdated, setCurrentModuleId, setModule } =
        useContext(ModuleContext);
    const { moduleId: id } = useParams();

    const setFavorite = useSetModuleFavorite();
    const setUnfavorite = useSetModuleUnfavorite();
    const hasDocuments = useMemo(
        () => module?.documents && module?.documents?.length > 0,
        [module]
    );
    const suggestions = useMemo(
        () => pathwayModules?.filter((mod) => mod.id !== module?.id),
        [pathwayModules, module]
    );

    const duration = useMemo(
        () =>
            module?.units?.reduce((unitAcc, unit) => {
                return (unitAcc += unit.items.reduce((itemAcc, item) => {
                    return (itemAcc += !item || !item.duration ? 0 : item.duration);
                }, 0));
            }, 0),
        [module]
    );

    const completedUnits = useMemo(() => {
        return (
            module?.unitsProgress?.reduce((completed, unit) => {
                const numberItems = module?.units?.find((u) => u.id === unit.id)?.items.length ?? 1;

                return (completed += unit.completedItems === numberItems ? 1 : 0);
            }, 0) ?? 0
        );
    }, [module]);

    const nextUnitId = useMemo(() => {
        const [nextUnit] =
            module?.units?.filter((unit) => !unit.isCompleted) ?? module?.units ?? [];

        return nextUnit?.id ?? module?.units?.[0].id;
    }, [module]);

    useEffect(() => {
        setCurrentModuleId(id);

        return () => {
            setCurrentModuleId(undefined);
            setModule(undefined);
        };
    }, [id]);

    const handleFavorite = async () => {
        if (!id || !module) return;
        setIsLoading(true);

        if (!module.isFavorite) await setFavorite.mutateAsync(id);
        else await setUnfavorite.mutateAsync(id);
        setUpdated(new Date());
        setIsLoading(false);
    };
    
    return (
        <>
            {!outlet && (
                <div className="d-flex flex-column p-11 p-md-2 px-lg-0 mb-md-6 pb-1">
                    <div className="row gy-22">
                        <div className="col-12 col-lg-3 d-flex align-items-start ps-lg-21">
                            <Link
                                to={".."}
                                icon="Chevron--left"
                                iconSize={16}
                                customCss="p-0 ms-lg-2 mt-n1 d-flex align-items-center gap-01 color-neutrals-black text-bd2-rg">
                                {translate("common.back", "Back")}
                            </Link>
                        </div>
                        <div className="col-12 col-lg-6">
                            <section className="module d-flex flex-column gap-11">
                                <div className="d-flex flex-column gap-02">
                                    <div className="d-flex justify-content-between">
                                        <span
                                            className={`color-${module?.tag_color ?? "yellow"}-500 text-bd4-bd text-uppercase`}>
                                            {module?.tag}
                                        </span>
                                        <FavoriteButton
                                            className="ms-auto"
                                            isFavorite={module?.isFavorite}
                                            onClick={handleFavorite}
                                        />
                                    </div>
                                    <h1 className="text-h1-bd">{module?.title}</h1>
                                    <div className="d-flex justify-content-between">
                                        <span className="d-flex gap-01 align-items-center color-neutrals-dark text-bd3-rg">
                                            <i className={`icon icon-16 icon-Time`}></i>
                                            {duration}
                                            min {translate("common.article", "article")}
                                        </span>
                                        {module?.units && (
                                            <span className="color-neutrals-dark text-bd3-rg">
                                                {`${!module?.unitsProgress ? 0 : completedUnits} / ${module?.units?.length} ${
                                                    module?.units?.length > 1
                                                        ? translate("common.units", "units")
                                                        : translate("common.unit", "unit")
                                                }`}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <figure>
                                    <img
                                        className="module_thumbnail"
                                        src={
                                            module?.image?.url
                                                ? `${CONTENT_BASE_URL}${module?.image?.url}`
                                                : Placeholder
                                        }
                                        alt={module?.image?.alt ?? module?.title}
                                    />
                                </figure>
                                <div className="mt-0">
                                    <span
                                        className="module_description color-neutrals-dark text-bd2-rg"
                                        dangerouslySetInnerHTML={{
                                            __html: markdownToHtml(module?.description ?? "")
                                        }}
                                    />
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <ul className="d-flex flex-column gap-02">
                                        {module?.units?.map((unit, i) => (
                                            <li key={i}>
                                                <UnitCard
                                                    progress={module.unitsProgress?.find(
                                                        (u) => u.id === unit.id
                                                    )}
                                                    unit={unit}
                                                    index={i + 1}
                                                    moduleId={module.id}
                                                    moduleImage={
                                                        module.image?.url
                                                            ? `${CONTENT_BASE_URL}${module.image?.url}`
                                                            : Placeholder
                                                    }
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                    <Link
                                        variant="solid"
                                        customCss="w-100"
                                        to={getFromattedPath(routes.UNIT.path, {
                                            moduleId: module?.id,
                                            unitId: nextUnitId
                                        })}>
                                        <>
                                            {module?.moduleProgress
                                                ? translate(
                                                      "module.continue_module",
                                                      "Continue module"
                                                  )
                                                : translate("module.start_module", "Start module")}
                                        </>
                                    </Link>
                                </div>
                            </section>
                            {hasDocuments && (
                                <section className="py-2" aria-labelledby="documents">
                                    <h2 className="text-h5-smbd mb-12" id="documents">
                                        {translate(
                                            "modules.support_materials",
                                            "Support Materials"
                                        )}
                                    </h2>
                                    <div className="card card_documents bg-neutrals-white d-flex flex-column gap-02 p-1">
                                        <h3 className="text-h5-smbd mb-12">
                                            {translate("common.documents", "Documents")}
                                        </h3>
                                        <ul>
                                            {module?.documents?.map((doc, i) => (
                                                <li key={i} className="card_document d-flex">
                                                    <div className="d-flex flex-wrap gap-02 w-100 py-03">
                                                        <Button
                                                            type="button"
                                                            onClick={() =>
                                                                handleFileDownload(
                                                                    `${CONTENT_BASE_URL}${doc.url}`,
                                                                    doc.alt ?? doc.name
                                                                )
                                                            }
                                                            customCss="justify-content-start button card_title text-h5-smbd w-100 p-0  ">
                                                            {doc.alt ?? doc.name}
                                                        </Button>
                                                        <span className="color-neutrals-neutral text-bd3-rg text-uppercase">
                                                            {doc.ext?.replace(".", "")}
                                                        </span>
                                                        <span className="color-neutrals-neutral text-bd3-rg text-uppercase">
                                                            {Math.round(doc.size)}KB
                                                        </span>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        icon="Download"
                                                        iconSize={24}
                                                        onClick={() =>
                                                            handleFileDownload(
                                                                `${CONTENT_BASE_URL}${doc.url}`,
                                                                doc.alt ?? doc.name
                                                            )
                                                        }
                                                        customCss="justify-content-start button card_title text-h5-smbd p-0"></Button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </section>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {outlet && <Outlet />}
            {suggestions && suggestions?.length > 0 && (
                <div className="bg-blue-50 w-100 py-61 px-31 mt-auto d-none d-lg-block">
                    <h2 className="text-h4-md mb-12">
                        {translate("common.top_suggestions", "Top suggestions")}
                    </h2>
                    <div className="d-flex gap-12">
                        {suggestions
                            ?.filter((_, i) => i < 5)
                            .map((mod, i) => (
                                <ModuleCard key={i} {...mod} />
                            ))}
                    </div>
                </div>
            )}
            {suggestions && suggestions?.length > 0 && (
                <div className="bg-blue-50 w-100 px-11 py-22 py-md-61 px-md-31 mt-auto d-lg-none">
                    <div className="d-flex justify-content-between align-items-center mb-12">
                        <h2 className="text-h4-md ">
                            {translate("common.top_suggestions", "Top suggestions")}
                        </h2>
                        <Link
                            to={routes.EXPLORE.path}
                            variant="link"
                            icon="Chevron--right"
                            isIconRight
                            customCss="color-green-600">
                            {translate("common.view_all", "View all")}
                        </Link>
                    </div>
                    <div className="d-flex flex-column gap-02 gap-md-12">
                        {suggestions
                            ?.filter((_, i) => i < 3)
                            .map((mod, i) => (
                                <ModuleCard key={i} {...mod} isSmall />
                            ))}
                    </div>
                </div>
            )}
        </>
    );
}

export default Module;
